// Eskom PARCC — Cloud Storage upload/fetch glue (the Firebase-touching layer).
//
// Thin by design and not unit-tested (it only wires the SDK); the path + size +
// type rules it relies on are pure and tested in src/lib/storagePaths.ts, and
// the real authorization boundary is storage.rules.
//
// Reads use getBlob (NOT getDownloadURL): getBlob carries the caller's ID token
// and is enforced by storage.rules, so an object is only ever fetched by an
// authorised user — never via a shareable, rules-bypassing download-URL token.
// (This is why the bucket needs a CORS allow-list for the app origin — see
// storage.cors.json.)
import { ref, uploadBytes, getBlob } from 'firebase/storage';
import { storage } from './firebase';
import {
  avatarObjectPath,
  reportObjectPath,
  validateAvatarFile,
} from './storagePaths';

/**
 * Upload a user's avatar to `avatars/{uid}/{ts}-{name}` and return the stored
 * object path (NOT a URL) for persistence in the profile. Validates type + size
 * client-side first (the rules enforce the same server-side). The timestamp
 * prefix keeps successive uploads distinct so a replaced avatar is never served
 * from a stale cache.
 */
export async function uploadAvatar(uid: string, file: File): Promise<string> {
  const problem = validateAvatarFile(file);
  if (problem) throw new Error(problem);
  const path = avatarObjectPath(uid, `${isoStamp()}-${file.name}`);
  await uploadBytes(ref(storage, path), file, { contentType: file.type });
  return path;
}

/**
 * Upload a generated report blob to `reports/{uid}/{name}` and return the stored
 * object path. The caller (admin) is the generating user; the rules require an
 * admin token whose uid matches the prefix.
 */
export async function uploadReportObject(
  uid: string,
  fileName: string,
  blob: Blob,
  contentType: string,
): Promise<string> {
  const path = reportObjectPath(uid, fileName);
  await uploadBytes(ref(storage, path), blob, { contentType });
  return path;
}

/**
 * Fetch a stored object as an in-memory object URL for rendering (e.g. an avatar
 * `<img src>`). The returned URL is a local `blob:` reference — the caller should
 * revoke it (URL.revokeObjectURL) when the element unmounts. Throws if the caller
 * is not authorised (storage.rules) or the object is missing.
 */
export async function fetchStorageObjectUrl(path: string): Promise<string> {
  const blob = await getBlob(ref(storage, path));
  return URL.createObjectURL(blob);
}

/**
 * Fetch a stored object and trigger a browser download named `downloadName`.
 * Used to re-retrieve a previously generated, persisted report. Revokes the
 * transient object URL after the click.
 */
export async function downloadStorageObject(path: string, downloadName: string): Promise<void> {
  const blob = await getBlob(ref(storage, path));
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** A filesystem-safe ISO-ish timestamp (`2026-06-08T09-31-54-123Z`) for object names. */
function isoStamp(): string {
  return new Date().toISOString().replace(/[:.]/g, '-');
}
